import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button } from '@epoch-ui/components/Button/button';
import { Icon } from '@epoch-ui/components/Icon';
import { Question } from 'types/question.types';
import { ToasterVariant, useToaster } from 'hooks/useToaster';
import { QuestionFormValues } from '@event-editor/components/EditQuestionSideSheet/config/questionConfig';
import { AddRequiredQuestionConfirmationDialog } from '@event-editor/components/EditQuestionSideSheet/AddRequiredQuestionConfirmationDialog';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { useAddQuestionMutation } from '../mutations/useAddQuestionMutation';

interface DuplicateQuestionButtonProps {
  eventId: number;
  existingQuestion: Question;
  disabled?: boolean;
}

export const DuplicateQuestionButton: React.FC<DuplicateQuestionButtonProps> = ({
  eventId,
  existingQuestion,
  disabled
}) => {
  const { guestListTotals, preEventQuestions } = useEditableEvent();
  const confirmedGuests = guestListTotals?.confirmed ?? 0;
  const questionsLength = preEventQuestions?.filter((q) => !q.isDeleted).length || 0;

  const { handleSubmit, formState } = useFormContext<QuestionFormValues>();
  const { showToast } = useToaster();
  const [isAddRequiredQuestionConfirmationOpen, setIsAddRequiredQuestionConfirmationOpen] =
    useState(false);
  const [pendingQuestionData, setPendingQuestionData] = useState<Question | null>(null);

  const addQuestionMutation = useAddQuestionMutation(() =>
    showToast('Failed to duplicate question', { variant: ToasterVariant.ERROR })
  );

  const executeDuplicate = async (questionData: Question): Promise<void> => {
    await addQuestionMutation.mutateAsync({
      eventId,
      question: questionData,
      questionsLength
    });
    showToast('Question has been duplicated.');
  };

  const handleClick = async (formData: QuestionFormValues): Promise<void> => {
    const { id, isDeleted, ...questionDataWithoutId } = {
      ...existingQuestion,
      ...formData
    } as Question;

    // Check if we need to show confirmation for duplicating a required question
    const shouldShowConfirmation =
      confirmedGuests > 0 && (questionDataWithoutId.isRequired ?? false);
    if (shouldShowConfirmation) {
      setPendingQuestionData(questionDataWithoutId as Question);
      setIsAddRequiredQuestionConfirmationOpen(true);
    } else {
      await executeDuplicate(questionDataWithoutId as Question);
    }
  };

  const handleConfirmDuplicate = async (): Promise<void> => {
    setIsAddRequiredQuestionConfirmationOpen(false);
    if (pendingQuestionData) {
      await executeDuplicate(pendingQuestionData);
      setPendingQuestionData(null);
    }
  };

  return (
    <>
      <Button
        data-testid="duplicate-button"
        variant="secondary"
        size="sm"
        className="tw-rounded-xl"
        type="button"
        onClick={handleSubmit(handleClick, (errors) => {
          console.error(errors);
        })}
        disabled={
          !formState.isValid || formState.isSubmitting || addQuestionMutation.isPending || disabled
        }
      >
        <Icon name="copy" size={16} strokeWidth={2.5} />
        <span className="tw-ml-1">Duplicate</span>
      </Button>

      {/* Duplicate required question confirmation dialog */}
      <AddRequiredQuestionConfirmationDialog
        open={isAddRequiredQuestionConfirmationOpen}
        onOpenChange={setIsAddRequiredQuestionConfirmationOpen}
        onConfirm={handleConfirmDuplicate}
      />
    </>
  );
};
